/**
 * クライアントサイドのエントリポイント。
 *
 * 方針（design.md D4 / Risks）:
 *   UI 部品は data 属性ベースの汎用初期化に統一し、画面ごとの個別 JS を作らない。
 *   新しい画面を追加してもテンプレートに data 属性を書くだけで動く。
 *
 * 部分更新ナビゲーション（ADR-0011 / add-htmx-partial-navigation design.md）:
 *   htmx の hx-boost + hx-select（base.html で宣言）でシェルを保持したまま
 *   #lms-main だけを差し替える。サーバーは常に完全なページを返す。
 */
import htmx from "htmx.org";

// コードブロックのシンタックスハイライト配色（github-dark テーマ）。
// esbuild がこの CSS import を app.js の隣に app.css として抽出し、base.html が読み込む。
// 別テーマにしたい場合はこの import 先を差し替えるだけでよい（例: atom-one-dark.css）。
import "highlight.js/styles/github-dark.css";

import { initCodeBlocks } from "./content/code-block.js";
import { initQuiz } from "./content/quiz.js";
import { initTabs } from "./content/tabs.js";
import { initToc } from "./content/toc.js";
import { initLearningLog } from "./learning-log/index.js";
import {
  MAIN_REGION_ID,
  createMainRegionRecoveryGuard,
  fallbackUrl,
  shouldFallbackToFullNavigation,
  shouldFocusAfterSettle,
} from "./partial-nav-guard.js";
import { initAccordion } from "./ui/accordion.js";
import { initAsyncPanel } from "./ui/async-panel.js";
import { initAutoSubmit } from "./ui/auto-submit.js";
import { initClipboard } from "./ui/clipboard.js";
import { initConfirmSubmit } from "./ui/confirm-submit.js";
import { initCounter } from "./ui/counter.js";
import { closeDrawer, initDrawer } from "./ui/drawer.js";
import { DROPDOWN_OPEN_EVENT, closeAllDropdowns, initDropdown } from "./ui/dropdown.js";
import { initFeedbackBox } from "./ui/feedback-box.js";
import { initFileInput } from "./ui/file-input.js";
import { initLoginFormPersist } from "./ui/login-form-persist.js";
import { closeAllModals, initModal, initModalAutoOpen, initModalKeyboard } from "./ui/modal.js";
import { updateNavActive } from "./ui/nav-active.js";
import { initNormalize } from "./ui/normalize.js";
import { initPasswordToggle } from "./ui/password-toggle.js";
import { initRemoteList } from "./ui/remote-list.js";
import { initSidebarToggle } from "./ui/sidebar-toggle.js";
import { initStatusModal } from "./ui/status-modal.js";
import { initSubmitGuard } from "./ui/submit-guard.js";
import { exposeToastApi, initFlashToasts, initToastHost } from "./ui/toast.js";

/** htmx:configRequest の detail のうち、このアプリが参照する部分（htmx 2.0.10） */
interface HtmxConfigRequestDetail {
  /** hx-boost 由来のリクエスト（＝画面遷移の代替）なら true */
  boosted: boolean;
  /** リクエストヘッダー。ここへ書き込むと htmx が xhr.setRequestHeader に反映する */
  headers: Record<string, string>;
}

/** htmx:beforeSwap の detail のうち、このアプリが参照する部分（htmx 2.0.10） */
interface HtmxSwapDetail {
  xhr?: XMLHttpRequest;
  shouldSwap: boolean;
  /** hx-boost 由来のリクエストなら true */
  boosted?: boolean;
  /** 応答本文（xhr.response）。文字列以外になりうるため参照側で型を確かめる */
  serverResponse?: unknown;
  /** htmx が保持するリクエスト／応答のパス情報（フォールバック先 URL の決定に使う） */
  pathInfo?: {
    requestPath: string;
    finalRequestPath: string;
    responsePath: string | null;
  };
}

/** htmx:afterSettle の detail のうち、このアプリが参照する部分（htmx 2.0.10） */
interface HtmxSettleDetail {
  /** hx-boost によるページ遷移なら true（htmx v2 の responseInfo.boosted） */
  boosted?: boolean;
  /** 差し替え対象として指定された要素（outerHTML 差し替え後は DOM から外れた旧要素） */
  target?: Element;
}

/**
 * 部分更新後、新しいページの見出しへフォーカスを移す（受講者 #4）。
 *
 * h1 が無いページ（現状は該当なしだが将来の追加に備える）では #lms-main 自身を着地点にする。
 * スクロールは hx-swap="show:window:top" が afterSettle の後に行うため preventScroll を付け、
 * focus() 自身のスクロールと二重にならないようにする。
 * tabindex="-1" は付けたままにする（タブ順には入らず、次回以降の遷移でも再利用できる）。
 */
function focusMainHeading(main: HTMLElement): void {
  const landing = main.querySelector<HTMLElement>("h1") ?? main;
  landing.setAttribute("tabindex", "-1");
  landing.focus({ preventScroll: true });
}

/**
 * 動的に差し込まれた DOM に対しても再初期化できるよう公開する（assignment-submission
 * 05_ui.md §6.5。data-lms-async が差し込んだ断片の再初期化に使う）。
 */
export function initAll(scope: ParentNode = document): void {
  initAccordion(scope);
  initModal(scope);
  initStatusModal(scope);
  initDrawer(scope);
  initDropdown(scope);
  initClipboard(scope);
  initTabs(scope);
  initQuiz(scope);
  initCodeBlocks(scope);
  initToc(scope);
  initFileInput(scope);
  initFeedbackBox(scope);
  initAsyncPanel(scope, initAll);
  initNormalize(scope);
  initConfirmSubmit(scope);
  initRemoteList(scope);
  initCounter(scope);
  initAutoSubmit(scope);
  initPasswordToggle(scope);
  initSubmitGuard(scope);
  // 復元時に input を発火させるため、パスワード表示切替・送信ボタン活性の初期化より後に呼ぶ
  initLoginFormPersist(scope);
}

/**
 * ユーザーメニュー（ヘッダーのドロップダウン）とモバイルドロワーを同時に開かせない結線
 * （polish-menu-label-calendar D2 / auth spec「ユーザーメニューとドロワーが同時に開いた状態に
 * してはならない」）。
 *
 * ドロワー → メニューの向きは drawer.ts の openDrawer() が closeAllDropdowns() を直接呼ぶ。
 * 逆向きをここに置くのは、dropdown.ts が drawer.ts を import すると相互 import になるためで、
 * 部品どうしの結線を main.ts が持つのは htmx:beforeSwap の既存方針と同じ。
 *
 * boot() から 1 回だけ呼ぶ（initAll に入れると部分更新のたびにリスナーが二重登録される）。
 */
function initMenuExclusivity(): void {
  document.addEventListener(DROPDOWN_OPEN_EVENT, () => {
    closeDrawer();
  });
}

/** 部分更新ナビゲーションの設定とフック（design.md N4/N6/N7） */
function initPartialNavigation(): void {
  // 認証後の画面内容を localStorage に永続化しない（共用 PC を想定。design.md N6）。
  // 履歴キャッシュを持たないため、戻る/進むは全画面再取得にする。
  htmx.config.historyCacheSize = 0;
  htmx.config.refreshOnHistoryMiss = true;

  // 共通エラーページ（403/404/500/503）は base.html シェルを使う完全なページを返す
  // （tasks 9.0 / add-htmx-partial-navigation design.md Risks「boosted 遷移の 4xx/5xx 応答」）。
  // htmx v2 の既定 responseHandling は 4xx/5xx をスワップせず無反応になるため、
  // 2xx/3xx と同じくスワップする設定に上書きする（error フラグは残し、
  // htmx:responseError は従来どおり発火させる）。これによりシェルを保持したまま
  // #lms-main だけがエラーページの内容に差し替わり、履歴（URL）も更新される。
  htmx.config.responseHandling = [
    { code: "204", swap: false },
    { code: "[23]..", swap: true },
    { code: "[45]..", swap: true, error: true },
  ];

  // 回復ガードの前提条件: ページ読み込み時点で #lms-main が DOM に存在したか
  // （fix-partial-nav-login-reload-loop design.md D1。2026-08-06 追加）。前提条件の意味・
  // 判定対象ページ（/login 系）・発見経緯（AWS 本番 2026-08-06・GET /login?logout の
  // 無限リロードループ）は createMainRegionRecoveryGuard の JSDoc を参照（実装時の訂正・
  // 2026-08-06、typescript-reviewer Medium-1 指摘。両ファイルにほぼ同一文面が重複し
  // 片方だけ更新されて食い違うリスクがあったため、経緯説明は JSDoc 側に一本化した）。
  //
  // 評価は初期化時の 1 回でよい。本プロジェクトは SSR（Thymeleaf が常に完全なページを返す）で
  // あり、#lms-main はシェル付きページの初期 HTML に必ず含まれる（ADR-0011）。
  // **#lms-main を JS で後から挿入する構成を導入する場合はこの前提が崩れ、回復ガードが
  // 黙って無効になるため、fix-partial-nav-login-reload-loop design.md の改訂が必要になる**
  // （同 design.md Risks）。
  const mainRegionExistedAtBoot = document.getElementById(MAIN_REGION_ID) !== null;

  // 部分更新の完了後に #lms-main が DOM から失われていた場合の回復ガード（design.md D5）。
  // 回復（全画面遷移）は 1 回までに制限する。initPartialNavigation() は boot() から 1 回しか
  // 呼ばれないため、この判定器はページごとに 1 つだけ存在する。下記 MutationObserver（主経路）と
  // htmx:afterSettle（副次経路）の両方でこの同一インスタンスを共有し、二重遷移を防ぐ。
  // 前提条件（mainRegionExistedAtBoot）を判定器側に持たせている理由は
  // createMainRegionRecoveryGuard の JSDoc を参照（fix-partial-nav-login-reload-loop design.md D2）。
  const shouldRecoverMainRegion = createMainRegionRecoveryGuard(mainRegionExistedAtBoot);

  // 起動時に #lms-main を持たないページでは MutationObserver を登録しない
  // （fix-partial-nav-login-reload-loop design.md D1 の 1）。判定器が常に false を返すため
  // 登録したままでも挙動は正しいが、シェルなしページで全 DOM 変異を監視し続けるのは純粋な
  // 無駄なので登録自体を止める。**この登録スキップと判定器側の前提条件チェックは二層の防御**
  // であり、判定器側は「万一この登録条件が将来変わっても暴発しない」ための備えとして残す
  // （CLAUDE.md の多層防御方針と同型。どちらか一方だけを削ってはならない）。
  // ここで initPartialNavigation() から return してはならない: 後続の htmx イベントリスナー
  // 3 本（configRequest / beforeSwap / afterSettle）はシェルなしページでも従来どおり登録し、
  // 挙動を変えない（同 design.md D3）。
  if (mainRegionExistedAtBoot) {
    // DOM 変異監視による回復ガードの主経路（design.md D5 改訂・2026-08-04・FPN-BR-05 実機確認起因）。
    //
    // htmx 2.0.10 は htmx:afterSettle を settleInfo.elts の各要素へ直接 dispatchEvent する
    // （htmx.js:1996-2001 / 3102-3126）。#lms-main が swap 直後に DOM から切り離された場合、
    // その要素は親を持たないため、bubbles: true でもイベントの伝播経路は自要素だけで完結し
    // document.body には届かない（event path は dispatch 時点の親鎖から構築されるという DOM 仕様上の
    // 制約であり、document 側で capture: true 登録しても受け取れない）。
    // つまり「#lms-main が消えたこと」を htmx イベント経由で検知しようとすると、
    // 検知したい対象が消えた瞬間にガード自体も沈黙してしまう構造的欠陥がある（下記 htmx:afterSettle
    // の項を参照）。この主経路は htmx のイベントに依存せず DOM の変化そのものを監視することで
    // この欠陥を回避する。
    //
    // MutationObserver のコールバックはブラウザが変異をバッチ処理してから呼ばれるため、
    // 1 回の swap につき O(1) のコールバック呼び出しで済み、htmx の発火順や settleDelay の設定にも
    // 依存しない。subtree: true は「将来の拡張のための余裕」ではなく現時点で必須である
    // （実装時の訂正・2026-08-04、typescript-reviewer Medium-3 指摘）: base.html の実構造は
    // <body> > .lms-shell > .lms-body > <main id="lms-main"> であり、#lms-main は
    // document.body の 3 階層下にある。outerHTML swap は #lms-main を .lms-body の子として
    // 差し替える（childList の変異が起きるのは .lms-body であって document.body ではない）ため、
    // subtree: false（childList だけ）では document.body 直下の変化しか見えず、
    // 現行の主要シナリオ（#lms-main 自体の着脱）そのものを検知できない。外してはならない。
    const mainRegionObserver = new MutationObserver(() => {
      if (shouldRecoverMainRegion(document.getElementById(MAIN_REGION_ID) !== null)) {
        console.error(
          "[partial-nav] 部分更新の完了後に #lms-main が DOM に存在しません。" +
            "全画面遷移で回復します（fix-partial-nav-missing-main-fallback design.md D5）。",
        );
        mainRegionObserver.disconnect();
        window.location.assign(window.location.href);
      }
    });
    mainRegionObserver.observe(document.body, { childList: true, subtree: true });
  }

  // boosted リクエストは「ブラウザの画面遷移の代替」なので、コンテンツネゴシエーションも
  // ブラウザと同じく HTML を要求する（harden-partial-navigation design.md H1 / issue #1）。
  // htmx は Accept を送らないため既定の */* になり、response.sendError() 経由のエラー応答
  // （CSRF 403・未マップ URL 404・想定外の 500）を BasicErrorController が JSON と判定していた。
  // JSON には #lms-main が無いため outerHTML swap が #lms-main を DOM から削除し、画面が白紙化した。
  // detail.boosted で限定するのは、将来 hx-get で JSON API を叩く拡張をしたときに
  // Accept を汚染しないため（design.md H1）。
  document.body.addEventListener("htmx:configRequest", (e) => {
    const detail = (e as CustomEvent<HtmxConfigRequestDetail>).detail;
    if (detail.boosted) {
      detail.headers["Accept"] = "text/html";
    }
  });

  document.body.addEventListener("htmx:beforeSwap", (e) => {
    // 差し替え領域外の UI 状態（開いたモーダル・ドロワー・ドロップダウン）を遷移後に残さない
    closeAllModals();
    closeDrawer();
    closeAllDropdowns();

    const detail = (e as CustomEvent<HtmxSwapDetail>).detail;

    // セッション切れ等でログイン画面へリダイレクトされた場合は
    // シェル内に埋め込まず全画面遷移へフォールバックする（design.md N7）。
    // 後段の #lms-main ガードより先に評価し、二重に遷移させないため return する
    const responseUrl = detail.xhr?.responseURL;
    if (responseUrl && new URL(responseUrl).pathname === "/login") {
      detail.shouldSwap = false;
      window.location.assign(responseUrl);
      return;
    }

    // 応答に #lms-main が無いまま outerHTML swap を行うと、htmx は空の断片を挿入したうえで
    // 差し替え対象を無条件に削除し、#lms-main が DOM から失われて画面が白紙になる（issue #1）。
    // ここで swap を中止し、その応答の URL への全画面遷移にフォールバックする
    // （harden-partial-navigation design.md H2）。
    //
    // 上の Accept 上書き（H1）により通常のエラー応答はシェル付き HTML で返るため一見冗長だが、
    // 前段プロキシが返すエラーページ・メンテナンスページなど #lms-main を含まない応答は
    // 今後も起こりうる。「#lms-main は常に DOM に存在する」を不変条件として守る多層防御であり、
    // CLAUDE.md の方針（@NotBlank があっても strip() 後の空判定を削らない）と同型である。
    // H1 があるからといって、このガードを削ってはならない。
    //
    // 判定条件から detail.shouldSwap（htmx 内部の swap 可否判定）を外してある
    // （fix-partial-nav-missing-main-fallback design.md D2 / 実機確認 HPN-BR-12）。
    // 旧実装は「boosted かつ shouldSwap かつ #lms-main なし」の 3 条件 AND だったため、
    // htmx が先に shouldSwap を false にする経路ではフォールバックが発火せず、
    // URL だけ更新されて本文が空白・シェルだけ残る白紙化が実機で 2 回再現した。
    // 確定仕様も「hx-select の一致有無および htmx 内部の swap 可否判定の結果にかかわらず
    // 発動しなければならない」と定めている。判定の本体は partial-nav-guard.ts にある。
    //
    // shouldSwap = false を location.assign より先に立てることで、htmx が swap 処理の冒頭で
    // 行う履歴更新（pushUrlIntoHistory）ごと抑止し、「URL だけが遷移先」の状態を作らない。
    if (
      shouldFallbackToFullNavigation({
        boosted: detail.boosted,
        status: detail.xhr?.status,
        serverResponse: detail.serverResponse,
      })
    ) {
      detail.shouldSwap = false;
      window.location.assign(fallbackUrl(detail));
      return;
    }
  });

  // 差し替え完了後、挿入された DOM の UI 部品を再初期化する（design.md N4/N5）
  document.body.addEventListener("htmx:afterSettle", (e) => {
    const main = document.getElementById(MAIN_REGION_ID);
    if (main) {
      initAll(main);
      initFlashToasts(main);
    }
    updateNavActive();
    // 部分更新遷移のたびに即時反映する。タイマーは timerStarted ガードにより 1 本しか張られない
    // （learning-log 08_client-state-machine.md §7.1）。
    initLearningLog();

    // ここから下はページ遷移固有の後処理（受講者 #4）。既存の再初期化を必ず終えてから行う。
    const detail = (e as CustomEvent<HtmxSettleDetail>).detail;
    if (main && shouldFocusAfterSettle({ boosted: detail.boosted, targetId: detail.target?.id })) {
      focusMainHeading(main);
    }

    // 副次の検知経路（多層防御。fix-partial-nav-missing-main-fallback design.md D5 改訂・
    // 2026-08-04・FPN-BR-05 起因）。主経路は上記 initPartialNavigation() 冒頭の
    // MutationObserver（mainRegionObserver）である。
    //
    // afterSettle は swap 対象要素が DOM に接続されている場合にしか document.body へ届かない
    // （#lms-main 自体が切り離された経路では発火せず、単独では FPN-R4 を満たせないことを
    // FPN-BR-05 の実機検証で確認した）。ここに残すのは、swap 対象が #lms-main そのものではない
    // 経路（将来 hx-target を #lms-main の子孫に広げた場合等）で afterSettle は届くが
    // #lms-main は失われている、という副次的なケースのための多層防御である。
    // **主経路（MutationObserver）があっても、この副次経路を削ってはならない。**
    // 一回性フラグ（shouldRecoverMainRegion）は上記 MutationObserver と同一インスタンスを
    // 共有しているため、どちらが先に発火しても二重遷移は起きない。
    //
    // 判定器は「ページ読み込み時点で #lms-main が存在したページ」でのみ回復を許可する前提条件を
    // 内包しているため（fix-partial-nav-login-reload-loop design.md D2）、シェルなしページ
    // （/login 系）ではこの副次経路も自動的に無効になる。**ここに mainRegionExistedAtBoot の
    // 個別チェックを追加しないこと**（同 design.md D2・tasks 3.3。判定を 2 か所に分散させると
    // 将来どちらか一方だけが直され乖離する）。
    // 原因調査のため console.error に理由を残す（正常時は 1 度も出力されない）。
    if (shouldRecoverMainRegion(main !== null)) {
      console.error(
        "[partial-nav] 部分更新の完了後に #lms-main が DOM に存在しません。" +
          "全画面遷移で回復します（fix-partial-nav-missing-main-fallback design.md D5）。",
      );
      window.location.assign(window.location.href);
    }
  });
}

function boot(): void {
  // トーストの live region（視覚非表示のアナウンサー）と共有スタックを最初に常設する（部品 #4）。
  // live region は中身より前のタスクで DOM に置く必要がある。
  initToastHost();
  exposeToastApi();
  initModalKeyboard();
  initMenuExclusivity();
  initPartialNavigation();
  initAll(document);
  initModalAutoOpen(document);
  // フラッシュのトースト化は live region（アナウンサー）の挿入とは別タスクで行う（部品 #4）。
  // 同一タスクだと region と中身が同時挿入となり、支援技術が初期状態とみなして読み上げない。
  // 待ち時間ではなくタスク境界が要件なので 0ms でよい（表示上の変化はない）。
  // なお showToast() 側でも本文追加を別タスクへ回しているため、この遅延は二重の保証になる
  // （unify-study-toast-stacking design.md D1）。
  window.setTimeout(() => initFlashToasts(document), 0);
  // トグルボタン・サイドバーはシェル側（#lms-main の外）にあり部分更新で再描画されないため、
  // 起動時に 1 回だけ初期化する（initAll には入れない・SHELL-R4）。
  initSidebarToggle();
  updateNavActive();
  // 学習ログの 15 秒周期チェッカーを起動する（learning-log 08_client-state-machine.md §7.1）。
  initLearningLog();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
  boot();
}
